import { clsx, EMPTY_STRING, noop, type WithDataTestId } from "@regrello/core-utils";
import { DropdownMenu, type Popover } from "radix-ui";
import type React from "react";
import { useEffect } from "react";
import { RegrelloLinkV2 } from "../../text/link/RegrelloLinkV2";
import { RegrelloIcon } from "../../visual/icons/RegrelloIcon";
import { RegrelloMenuItemContent, type RegrelloMenuItemContentProps } from "./_internal/RegrelloMenuItemContent";
import { RegrelloMenuShortcut } from "./_internal/RegrelloMenuShortcut";
import { MENU_ITEM_SEPARATOR_CLASSES, menuItemVariants } from "./_internal/regrelloMenuUtils";

// (clewis): I tried for a while to conditionally hide a <RegrelloIcon iconName="blank" /> in radio
// groups if the parent had [data-state="checked"], following the Tailwind documentation, but for
// some reason nothing I tried was working:
//
// - hidden didn't generate "display: none" as expected
// - group-data-[state=checked]:hidden or any variant of that syntax didn't work
// - peer-data-[state=checked]:hidden or any variant of that syntax didn't work
const SMALL_ICON_WIDTH_CLASS = "w-5";

const RegrelloMenuRadioGroup = DropdownMenu.RadioGroup;

interface RegrelloMenuProps extends React.ComponentPropsWithoutRef<typeof DropdownMenu.Root> {
  /** Where along the specified {@see side} of the target to align the menu. */
  align?: Popover.PopoverContentProps["align"];

  /** The element that should open the menu when clicked. */
  children: React.ReactNode;

  /** The content to display in the menu. */
  content: React.ReactNode;

  /** Props to pass to the `content` element. */
  contentProps?: Omit<React.ComponentPropsWithoutRef<typeof DropdownMenu.Content>, "align" | "side">;

  /** The side of the target on which to align the menu content. */
  side?: Popover.PopoverContentProps["side"];

  /** Props to pass to the trigger element. */
  triggerProps?: React.ComponentPropsWithoutRef<typeof DropdownMenu.Trigger>;
}

function RegrelloMenu({ align, children, content, contentProps, side, triggerProps, ...props }: RegrelloMenuProps) {
  return (
    <DropdownMenu.Root {...props}>
      <DropdownMenu.Trigger asChild={true} {...triggerProps}>
        {children}
      </DropdownMenu.Trigger>
      <RegrelloMenuContent align={align} side={side} {...contentProps}>
        {content}
      </RegrelloMenuContent>
    </DropdownMenu.Root>
  );
}

type RegrelloMenuItemWithSubMenuProps = React.ComponentProps<typeof DropdownMenu.SubTrigger> &
  Omit<RegrelloMenuItemContentProps, "endElement"> & {
    /** The content to show in the submenu. */
    submenuContent?: React.ReactNode;
  };

function RegrelloMenuItemWithSubMenu({
  className,
  icon,
  inset,
  intent,
  ref,
  secondaryText,
  selected: isSelected,
  submenuContent,
  text,
  ...props
}: RegrelloMenuItemWithSubMenuProps) {
  return (
    <DropdownMenu.Sub>
      <DropdownMenu.SubTrigger
        ref={ref}
        className={clsx(menuItemVariants({ intent, isDisabled: props.disabled, isSelected }), className)}
        {...props}
      >
        <RegrelloMenuItemContent
          endElement={<RegrelloIcon iconName="chevron-right" intent={intent} />}
          icon={icon}
          inset={inset}
          intent={intent}
          secondaryText={secondaryText}
          text={text}
        />
      </DropdownMenu.SubTrigger>
      <DropdownMenu.Portal>
        <RegrelloMenuSubContent>{submenuContent}</RegrelloMenuSubContent>
      </DropdownMenu.Portal>
    </DropdownMenu.Sub>
  );
}

function RegrelloMenuSubContent({ className, ref, ...props }: React.ComponentProps<typeof DropdownMenu.SubContent>) {
  return (
    <DropdownMenu.SubContent
      ref={ref}
      className={clsx(
        "z-tooltip min-w-32 overflow-auto rounded border bg-background p-1 shadow-lg data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
        className,
      )}
      style={{
        maxHeight: "var(--radix-dropdown-menu-content-available-height)",
        maxWidth: "var(--radix-dropdown-menu-content-available-width)",
      }}
      {...props}
    />
  );
}

/** The content to display in the menu popover. */
function RegrelloMenuContent({
  className,
  ref,
  sideOffset = 4,
  ...props
}: React.ComponentProps<typeof DropdownMenu.Content>) {
  return (
    <DropdownMenu.Portal>
      <DropdownMenu.Content
        ref={ref}
        className={clsx(
          `
        z-tooltip
        shadow-md

        min-w-50
        p-1
        rounded
        border
        bg-background

        data-[state=open]:animate-in
        data-[state=open]:fade-in-0
        data-[state=open]:zoom-in-95

        data-[state=closed]:animate-out
        data-[state=closed]:fade-out-0
        data-[state=closed]:zoom-out-95

        data-[side=bottom]:slide-in-from-top-2
        data-[side=left]:slide-in-from-right-2
        data-[side=right]:slide-in-from-left-2
        data-[side=top]:slide-in-from-bottom-2
        `,

          // (clewis): Allow users to scroll through menu content if the menu height exceeds the
          // viewport height.
          `
        overflow-auto
        `,
          className,
        )}
        sideOffset={sideOffset}
        style={{
          maxHeight: "var(--radix-dropdown-menu-content-available-height)",
          maxWidth: "var(--radix-dropdown-menu-content-available-width)",
        }}
        {...props}
      />
    </DropdownMenu.Portal>
  );
}

type RegrelloMenuItemProps = React.ComponentProps<typeof DropdownMenu.Item> &
  WithDataTestId &
  Omit<RegrelloMenuItemContentProps, "endElement"> & {
    /** An optional keyboard shortcut to display to the right of the main `text`. */
    shortcut?: React.ReactNode;

    /**
     * A URL to navigate to on click, instead of invoking `onClick`. If provided, a React
     * Router-powered `RegrelloLinkV2` child will be rendered and the menu item will set
     * `asChild={true}`.
     */
    to?: string;
  };

function RegrelloMenuItem({
  className,
  dataTestId,
  disabled: isDisabled,
  icon,
  inset = false,
  intent,
  onClick,
  ref,
  secondaryText,
  selected: isSelected,
  shortcut,
  text,
  to,
  tooltip,
  ...props
}: RegrelloMenuItemProps) {
  useEffect(() => {
    if (icon != null && inset) {
      console.warn("[RegrelloMenuItemV2] Do not pass `inset={true}` when an `icon` is also provided.");
    }
  }, [icon, inset]);

  const itemContent = (
    <RegrelloMenuItemContent
      endElement={shortcut != null && <RegrelloMenuShortcut intent={intent}>{shortcut}</RegrelloMenuShortcut>}
      icon={icon}
      inset={inset}
      intent={intent}
      secondaryText={secondaryText}
      text={text}
      tooltip={tooltip}
    />
  );

  const menuItemClassName = clsx(menuItemVariants({ intent, isDisabled, isSelected }), className);

  return (
    <DropdownMenu.Item
      ref={ref}
      asChild={to != null}
      className={menuItemClassName}
      data-testid={dataTestId ?? EMPTY_STRING}
      disabled={isDisabled}
      // (dosipiuk): This is a fix specific for Firefox as it fails to disable the handler
      onClick={isDisabled ? noop : onClick}
      {...props}
    >
      {to != null ? (
        // (clewis): Must display as block or flex rather than the default "inline", else
        // hover/focus styles do not show up.
        <RegrelloLinkV2 className={clsx(menuItemClassName, "flex")} to={to} unstyled={true}>
          {itemContent}
        </RegrelloLinkV2>
      ) : (
        itemContent
      )}
    </DropdownMenu.Item>
  );
}

type RegrelloMenuLinkItemProps = React.ComponentProps<typeof DropdownMenu.Item> &
  Omit<RegrelloMenuItemContentProps, "endElement"> &
  WithDataTestId & {
    /** An optional keyboard shortcut to display to the right of the main `text`. */
    shortcut?: string;
    to: string;
    state?: Record<string, unknown>;
  };

function RegrelloMenuLinkItem({
  className,
  dataTestId,
  icon,
  inset = false,
  intent,
  ref,
  secondaryText,
  selected: isSelected,
  shortcut,
  state,
  text,
  to,
  ...props
}: RegrelloMenuLinkItemProps) {
  useEffect(() => {
    if (icon != null && inset) {
      console.warn("[RegrelloMenuItemV2] Do not pass `inset={true}` when an `icon` is also provided.");
    }
  }, [icon, inset]);

  return (
    <DropdownMenu.Item
      ref={ref}
      className={clsx(menuItemVariants({ intent, isDisabled: props.disabled, isSelected }), className)}
      {...props}
    >
      <RegrelloLinkV2
        className="text-inherit hover:no-underline font-normal"
        dataTestId={dataTestId}
        state={state}
        to={to}
      >
        <RegrelloMenuItemContent
          endElement={shortcut != null && <RegrelloMenuShortcut intent={intent}>{shortcut}</RegrelloMenuShortcut>}
          icon={icon}
          inset={inset}
          intent={intent}
          secondaryText={secondaryText}
          text={text}
        />
      </RegrelloLinkV2>
    </DropdownMenu.Item>
  );
}

type RegrelloMenuCheckboxItemProps = React.ComponentProps<typeof DropdownMenu.CheckboxItem> &
  Omit<RegrelloMenuItemContentProps, "endElement" | "icon" | "inset"> & {
    /** An optional keyboard shortcut to display to the right of the main `text`. */
    shortcut?: string;
  };

function RegrelloMenuCheckboxItem({
  checked,
  className,
  intent,
  ref,
  secondaryText,
  selected: isSelected,
  shortcut,
  text,
  ...props
}: RegrelloMenuCheckboxItemProps) {
  return (
    <DropdownMenu.CheckboxItem
      ref={ref}
      checked={checked}
      className={clsx(menuItemVariants({ intent, isDisabled: props.disabled, isSelected }), className)}
      {...props}
    >
      <RegrelloMenuItemContent
        endElement={shortcut != null && <RegrelloMenuShortcut intent={intent}>{shortcut}</RegrelloMenuShortcut>}
        icon={
          checked ? (
            <DropdownMenu.ItemIndicator>
              <RegrelloIcon iconName="selected" />
            </DropdownMenu.ItemIndicator>
          ) : (
            <RegrelloIcon iconName="blank" />
          )
        }
        intent={intent}
        secondaryText={secondaryText}
        text={text}
      />
    </DropdownMenu.CheckboxItem>
  );
}

function RegrelloMenuRadioItem({
  className,
  dataTestId,
  intent,
  ref,
  secondaryText,
  selected: isSelected,
  shortcut,
  text,
  ...props
}: React.ComponentProps<typeof DropdownMenu.RadioItem> &
  WithDataTestId &
  Omit<RegrelloMenuItemContentProps, "endElement" | "icon" | "inset"> & {
    /** An optional keyboard shortcut to display to the right of the main `text`. */
    shortcut?: string;
  }) {
  return (
    <DropdownMenu.RadioItem
      ref={ref}
      className={clsx(menuItemVariants({ intent, isDisabled: props.disabled, isSelected }), className)}
      data-testId={dataTestId}
      {...props}
    >
      <RegrelloMenuItemContent
        endElement={shortcut != null && <RegrelloMenuShortcut intent={intent}>{shortcut}</RegrelloMenuShortcut>}
        icon={
          <div className={SMALL_ICON_WIDTH_CLASS}>
            <DropdownMenu.ItemIndicator className="peer">
              <RegrelloIcon iconName="dot" intent={intent} />
            </DropdownMenu.ItemIndicator>
          </div>
        }
        intent={intent}
        secondaryText={secondaryText}
        text={text}
      />
    </DropdownMenu.RadioItem>
  );
}

function RegrelloMenuLabel({ className, ref, ...props }: React.ComponentProps<typeof DropdownMenu.Label>) {
  return <DropdownMenu.Label ref={ref} className={clsx("p-2 text-sm font-semibold", className)} {...props} />;
}

function RegrelloMenuSeparator({ className, ref, ...props }: React.ComponentProps<typeof DropdownMenu.Separator>) {
  return <DropdownMenu.Separator ref={ref} className={clsx(MENU_ITEM_SEPARATOR_CLASSES, className)} {...props} />;
}

type RegrelloMenuGroupProps = React.ComponentProps<typeof DropdownMenu.Group> & {
  /** The content to show in the group. */
  children: React.ReactNode;

  /**
   * Whether to show a separator above this group.
   *
   * @default false
   */
  showSeparator?: boolean;

  /** An optional title to display for this group. */
  title?: string;

  /** Class name to be attached to group title */
  titleProps?: {
    className?: string;
    "data-testid"?: string;
  };
};

function RegrelloMenuGroup({
  children,
  ref,
  showSeparator = false,
  title,
  titleProps,
  ...props
}: RegrelloMenuGroupProps) {
  return (
    <DropdownMenu.Group ref={ref} {...props}>
      {showSeparator ? <RegrelloMenuSeparator /> : null}
      {title != null ? <RegrelloMenuLabel {...titleProps}>{title}</RegrelloMenuLabel> : null}
      {children}
    </DropdownMenu.Group>
  );
}

const RegrelloMenuRoot = DropdownMenu.Root;
const RegrelloMenuTrigger = DropdownMenu.Trigger;

export {
  RegrelloMenu,
  RegrelloMenuCheckboxItem,
  RegrelloMenuContent,
  RegrelloMenuGroup,
  RegrelloMenuItem,
  RegrelloMenuItemContent,
  RegrelloMenuItemWithSubMenu,
  RegrelloMenuLabel,
  RegrelloMenuLinkItem,
  RegrelloMenuRadioGroup,
  RegrelloMenuRadioItem,
  RegrelloMenuRoot,
  RegrelloMenuSeparator,
  RegrelloMenuTrigger,
};

export type {
  RegrelloMenuCheckboxItemProps,
  RegrelloMenuGroupProps,
  RegrelloMenuItemProps,
  RegrelloMenuItemWithSubMenuProps,
  RegrelloMenuLinkItemProps,
  RegrelloMenuProps,
};
